import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  IconButton,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Stack,
  Avatar,
  Chip,
  useMediaQuery,
} from "@mui/material";
import { Delete, Add, Remove } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useCart } from "../../context/CartContext";
import { useOrders } from "../../context/OrderContext";
import { useState } from "react";

export default function Cart() {
  const { items, removeItem, clearCart, increaseQty, decreaseQty } = useCart();
  const { createOrder } = useOrders();

  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const subtotal = items.reduce(
    (sum, item) => sum + Number(item.price) * Number(item.quantity),
    0
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const handleCheckout = async () => {
    try {
      setLoading(true);
      await createOrder(items, subtotal);
      clearCart();
      setSnackbar({
        open: true,
        message: "✅ Order successfully created!",
        severity: "success",
      });
      setOpenDialog(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "❌ Error creating order, please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const QtyControl = ( {sku} ) => (
    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
      <IconButton size="small" onClick={() => decreaseQty(sku)}>
        <Remove />
      </IconButton>

      <Typography sx={{ minWidth: 22, textAlign: "center" }}>
        {items.find((x) => String(x.sku) === String(sku))?.quantity ?? 0}
      </Typography>

      <IconButton size="small" onClick={() => increaseQty(sku)}>
        <Add />
      </IconButton>
    </Box>
  );

  return (
    <Box sx={{ width: "100%", p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Paper
        sx={{
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          boxShadow: 3,
          overflow: "hidden",
        }}
      >
        {items.length === 0 ? (
          <Typography>
            Your cart is empty. Try adding some items or go to Orders and copy
            the items from it!
          </Typography>
        ) : (
          <>
            {/* ====== DESKTOP TABLE ====== */}
            {!isMobile && (
              <Box sx={{ width: "100%", overflowX: "auto" }}>
                <Table sx={{ minWidth: 860 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Product</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Variant</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Sku</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Quantity</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Price</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Sub-Total</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Action</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {items.map((item) => (
                      <TableRow key={String(item.sku)}>
                        {/* Product cell with image + name */}
                        <TableCell>
                          <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                          >
                            <Avatar
                              variant="rounded"
                              src={item.image ?? undefined}
                              alt={item.productName}
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: 2,
                                flexShrink: 0,
                                bgcolor: "action.hover",
                              }}
                            />
                            <Box sx={{ minWidth: 0 }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  lineHeight: 1.2,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: 320,
                                }}
                              >
                                {item.productName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.productSku}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <Chip
                            label={item.variantName || "Original"}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        </TableCell>

                        {/* lo que se muestra como sku de producto */}
                        <TableCell>{item.productSku}</TableCell>

                        <TableCell align="center">
                          <QtyControl sku={String(item.sku)} />
                        </TableCell>

                        <TableCell align="right">
                          ${Number(item.price).toFixed(2)}
                        </TableCell>

                        <TableCell align="right">
                          ${(Number(item.price) * Number(item.quantity)).toFixed(
                            2
                          )}
                        </TableCell>

                        <TableCell align="center">
                          <IconButton
                            color="error"
                            onClick={() => removeItem(String(item.sku))}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* ====== MOBILE CARDS ====== */}
            {isMobile && (
              <Stack spacing={1.5}>
                {items.map((item) => (
                  <Paper
                    key={String(item.sku)}
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: "background.paper",
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar
                        variant="rounded"
                        src={item.image ?? undefined}
                        alt={item.productName}
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: 2,
                          bgcolor: "action.hover",
                          flexShrink: 0,
                        }}
                      />

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          {item.productName}
                        </Typography>

                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mt: 0.75 }}
                        >
                          <Chip
                            label={item.variantName || "Original"}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            SKU: {item.productSku}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          sx={{ mt: 1 }}
                        >
                          <Typography sx={{ fontWeight: 700 }}>
                            ${(Number(item.price) * Number(item.quantity)).toFixed(
                              2
                            )}
                          </Typography>

                          <Box display="flex" alignItems="center" gap={1}>
                            <QtyControl sku={String(item.sku)} />

                            <IconButton
                              color="error"
                              onClick={() => removeItem(String(item.sku))}
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Footer responsive */}
            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", sm: "center" }}
              gap={2}
            >
              <Typography variant="h6">
                Total: ${subtotal.toFixed(2)}
              </Typography>

              <Box
                display="flex"
                gap={2}
                justifyContent={{ xs: "stretch", sm: "flex-end" }}
              >
                <Button
                  fullWidth={isMobile}
                  variant="outlined"
                  color="error"
                  onClick={clearCart}
                >
                  Clear Cart
                </Button>

                <Button
                  fullWidth={isMobile}
                  variant="contained"
                  color="primary"
                  onClick={() => setOpenDialog(true)}
                >
                  Checkout
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      <Dialog open={openDialog} onClose={() => !loading && setOpenDialog(false)}>
        <DialogTitle>Confirm Checkout</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to generate an order with{" "}
            <strong>{items.length}</strong> items for a total of{" "}
            <strong>${subtotal.toFixed(2)}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            color="secondary"
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCheckout}
            color="primary"
            variant="contained"
            disabled={loading}
          >
            {loading ? "Processing..." : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
